/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import {
  IAny,
  ISummaryTableData,
  ISummaryTableDataObject,
  IUtilityBillBreakdownAPIResponse
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { formatCurrency } from '../Utils/Utils';
import {
  UnitSymbols,
  getConvertedValue,
  getConvertedValueForUtilitySummary,
  // getMaxConvertedValueFromArray,
  getMaxConvertedValueFromArrayForUtilitySummary
} from '../Utils/unitHandling/unitHandling';

export interface IRawTableColumn {
  name: string;
  unit: string | null;
  key: string;
}

interface IUtilityBillBreakdownExtended {
  data: IUtilityBillBreakdownAPIResponse;
}

export const processRowData: IAny = (
  rowData: ISummaryTableDataObject[],
  conversionType: string
) => {
  return rowData.map((dt: ISummaryTableDataObject) => {
    if (!dt.meterDetails) {
      dt.accountNumber = dt.meterName + '(' + dt.meterNumber + ')';
      dt.isMeter = true;
    }
    const obj: { [key: string]: string | null | number } = {};
    const billingDemandUnit = dt.dynamicColumnData.filter(
      (dtm) => dtm.key === 'billingDemandUnit'
    )[0].value;
    const consumptionUnit = dt.dynamicColumnData.filter(
      (dtm) => dtm.key === 'consumptionUnit'
    )[0].value;
    const recordedDemandUnit = dt.dynamicColumnData.filter(
      (dtm) => dtm.key === 'recordedDemandUnit'
    )[0].value;

    dt.dynamicColumnData.forEach((dny: { [key: string]: string }) => {
      if (dny.key === 'billingdemand') {
        obj[dny.key] = getConvertedValueForUtilitySummary(
          conversionType,
          billingDemandUnit,
          Number(dny.value),
          getMaxConvertedValueFromArrayForUtilitySummary(
            conversionType,
            billingDemandUnit,
            [Number(dny.value)],
            billingDemandUnit
          ).Unit
        ).Value;
        obj.scaledBillingDemandUnit = getMaxConvertedValueFromArrayForUtilitySummary(
          conversionType,
          billingDemandUnit,
          [Number(dny.value)],
          billingDemandUnit
        ).Unit;
      } else if (dny.key === 'consumption') {
        obj[dny.key] = getConvertedValueForUtilitySummary(
          conversionType,
          consumptionUnit,
          Number(dny.value),
          getMaxConvertedValueFromArrayForUtilitySummary(
            conversionType,
            consumptionUnit,
            [Number(dny.value)],
            consumptionUnit
          ).Unit
        ).Value;
        obj.scaledConsumptionUnit = getMaxConvertedValueFromArrayForUtilitySummary(
          conversionType,
          consumptionUnit,
          [Number(dny.value)],
          consumptionUnit
        ).Unit;
      } else if (dny.key === 'recordeddemand') {
        obj[dny.key] = getConvertedValueForUtilitySummary(
          conversionType,
          recordedDemandUnit,
          Number(dny.value),
          getMaxConvertedValueFromArrayForUtilitySummary(
            conversionType,
            recordedDemandUnit,
            [Number(dny.value)],
            recordedDemandUnit
          ).Unit
        ).Value;
        obj.scaledRecordDemandUnit = getMaxConvertedValueFromArrayForUtilitySummary(
          conversionType,
          recordedDemandUnit,
          [Number(dny.value)],
          recordedDemandUnit
        ).Unit;
      } else if (
        dny.key === 'billingDemandUnit' ||
        dny.key === 'consumptionUnit' ||
        dny.key === 'recordedDemandUnit'
      ) {
        obj[dny.key] = dny.value;
      } else {
        obj[dny.key] = formatCurrency(
          Number(dny.value),
          dt.utilityCostCurrency,
          true,
          2
        );
      }
    });
    if (!dt.meterDetails) {
      obj['totalcost'] = '';
    }
    if (dt.meterDetails) {
      dt.meterDetails = processRowData(dt.meterDetails, conversionType);
    } else {
      console.log('log');
    }
    return { ...dt, ...obj };
  });
};

export const skipIfAllZero = (rowData: IAny[]) => {
  return rowData.map((dt) => {
    const obj: IAny = {};
    Object.keys(dt).map((prop: IAny) => {
      if (prop === 'meterDetails') {
        obj[prop] = skipIfAllZero(dt[prop]);
      } else if (dt[prop] && dt[prop].toString() !== '0') {
        obj[prop] = dt[prop];
      } else {
        console.log('log');
      }
    });
    return obj;
  });
};

export const eliminateNoValueDynamicColumns = (
  rowData: IAny[],
  dynamicColumns: IAny
) => {
  return dynamicColumns.filter((column: IAny) => {
    const obj: IAny = {};
    let isPresent = false;
    rowData.forEach((dt: IAny) => {
      if (Object.keys(dt).indexOf(column.key) !== -1) {
        isPresent = true;
      } else if (dt.meterDetails) {
        dt.meterDetails.forEach((meter: IAny) => {
          if (Object.keys(dt).indexOf(column.key) !== -1) {
            isPresent = true;
          }
        });
      }
    });
    return isPresent;
  });
};

export const createDynamicColumnList = (rowData: ISummaryTableDataObject[]) => {
  const dynamicColumns: IRawTableColumn[] = [];
  rowData.map((dt: ISummaryTableDataObject) => {
    dt.dynamicColumnData.forEach((dny: { [key: string]: string }) => {
      if (
        dynamicColumns.findIndex((col: IAny) => col.name === dny.name) === -1
      ) {
        dynamicColumns.push({
          name: dny.name,
          key: dny.name,
          unit: dny.unit
        });
      }
    });
  });
  return dynamicColumns;
};

// export const prepareSummaryTableData = (rowData: ISummaryTableData) => {
//   rowData.data = processRowData(rowData.data);
//   rowData['dynamicColumns'] = createDynamicColumnList(rowData.data);
// };

export const createDynamicColummsFromExisting = (
  rowData: IRawTableColumn[]
) => {
  return rowData.map((dt: IRawTableColumn) => {
    return {
      name: dt.name,
      unit: dt.unit,
      key: dt.name
    };
  });
};
